import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { LogOut, Settings, Bell, BarChart3, Plus, Trash2, Edit, X, ExternalLink, Clock, Ticket } from 'lucide-react';
import Logo from './Logo';
import toast from 'react-hot-toast';
import './Dashboard.css';
import { apiRequest } from '../config/api';

export default function Dashboard() {
  const { currentUser, userProfile, logout, updateUserProfile } = useAuth();
  

  const [activeTab, setActiveTab] = useState('overview');

  // Debug: Log para ver el userProfile que llega del contexto
  useEffect(() => {
    console.log('🔍 Dashboard - userProfile desde contexto:', {
      plan: userProfile?.plan,
      frecuencia: userProfile?.configuracion?.frecuencia,
      partidos: userProfile?.configuracion?.partidos?.length,
      partidosData: userProfile?.configuracion?.partidos,
      onboardingCompletado: userProfile?.onboardingCompletado,
      fullProfile: userProfile
    });
  }, [userProfile]);
  
  // Estados para modales
  const [showAddMatchModal, setShowAddMatchModal] = useState(false);
  const [showThresholdModal, setShowThresholdModal] = useState(false);
  const [editingMatch, setEditingMatch] = useState(null);

  // Manejar pago exitoso
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get('payment');
    const plan = urlParams.get('plan');

    if (paymentStatus === 'success' && currentUser) {
      // Mostrar mensaje de éxito
      toast.success(`¡Pago exitoso! Plan ${plan} activado.`);
      
      // Actualizar onboarding como completado
      const updateOnboarding = async () => {
        try {
          // Actualizar en backend
          const response = await apiRequest('/api/users/onboarding-complete', {
            method: 'POST',
            body: { 
              plan: plan,
              onboardingCompletado: true 
            }
          });
          
          if (response.success) {
            // Actualizar contexto local
            await updateUserProfile(currentUser.uid);
          }
        } catch (error) {
          console.error('Error actualizando onboarding:', error);
          toast.error('Error al completar configuración');
        }
      };
      
      // Ejecutar actualización
      updateOnboarding().then(() => {
        // Limpiar parámetros de URL
        window.history.replaceState({}, document.title, '/dashboard');
      });
    }
  }, [currentUser, updateUserProfile]);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    }
  };

  const handleAddMatch = async (matchData) => {
    try {
      // Validar límites del plan
      const currentMatches = userProfile?.configuracion?.partidos || [];
      const maxMatches = userProfile?.plan === 'basico' ? 1 : 
                        userProfile?.plan === 'premium' ? 3 : 7;
      
      if (currentMatches.length >= maxMatches) {
        toast.error(`Tu plan permite máximo ${maxMatches} partidos`);
        return;
      }

      // Actualizar configuración
      const newConfig = {
        ...userProfile.configuracion,
        partidos: [...currentMatches, matchData]
      };

      // Actualizar en backend
      const response = await apiRequest('/api/users/config', {
        method: 'PUT',
        body: {
          partidos: newConfig.partidos,
          umbrales: newConfig.umbrales || {},
          frecuencia: newConfig.frecuencia || 10
        }
      });

      if (response.success) {
        // Actualizar el contexto
        await updateUserProfile(currentUser.uid);
        toast.success('Partido agregado exitosamente');
        setShowAddMatchModal(false);
      }
    } catch (error) {
      console.error('Error agregando partido:', error);
      toast.error('Error al agregar partido');
    }
  };

  const handleRemoveMatch = async (matchNo) => {
    try {
      const newPartidos = userProfile.configuracion.partidos.filter(
        partido => partido.matchNo !== matchNo
      );

      const newConfig = {
        ...userProfile.configuracion,
        partidos: newPartidos
      };

      // Actualizar en backend
      const response = await apiRequest('/api/users/config', {
        method: 'PUT',
        body: {
          partidos: newConfig.partidos,
          umbrales: newConfig.umbrales || {},
          frecuencia: newConfig.frecuencia || 10
        }
      });

      if (response.success) {
        // Actualizar el contexto
        await updateUserProfile(currentUser.uid);
        toast.success('Partido removido exitosamente');
      }
    } catch (error) {
      console.error('Error removiendo partido:', error);
      toast.error('Error al remover partido');
    }
  };

  return (
    <div className="dashboard">
      {/* Header */}
      <header className="dashboard-header">
        <div className="header-content">
          <div className="header-left">
            <Logo size="header-logo" className="header-logo" />
            <p>Panel de control</p>
          </div>
          <div className="header-right">
            <div className="user-info">
              <span>Hola, {userProfile?.username || currentUser?.displayName || 'Usuario'}</span>
              <span className="plan-badge">{userProfile?.plan || 'Gratuito'}</span>
            </div>
            <button onClick={handleLogout} className="btn btn-secondary logout-btn">
              <LogOut size={18} />
              Cerrar sesión
            </button>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="dashboard-nav">
        <div className="nav-content">
          <button 
            className={`nav-tab ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            <BarChart3 size={20} />
            Resumen
          </button>
          <button 
            className={`nav-tab ${activeTab === 'monitoring' ? 'active' : ''}`}
            onClick={() => setActiveTab('monitoring')}
          >
            <Bell size={20} />
            Monitoreo
          </button>
          <button 
            className={`nav-tab ${activeTab === 'tickets' ? 'active' : ''}`}
            onClick={() => setActiveTab('tickets')}
          >
            <Ticket size={20} />
            Monitoreo de Tickets
          </button>
          <button 
            className={`nav-tab ${activeTab === 'alerts' ? 'active' : ''}`}
            onClick={() => setActiveTab('alerts')}
          >
            <Bell size={20} />
            Alertas
          </button>
          <button 
            className={`nav-tab ${activeTab === 'settings' ? 'active' : ''}`}
            onClick={() => setActiveTab('settings')}
          >
            <Settings size={20} />
            Configuración
          </button>
        </div>
      </nav>

      {/* Main Content */}
      <main className="dashboard-main">
        <div className={`main-content ${activeTab === 'settings' ? 'fullwidth' : ''}`}>
          {activeTab === 'overview' && (
            <OverviewTab 
              userProfile={userProfile} 
              onAddMatch={() => setShowAddMatchModal(true)}
              onConfigureAlerts={() => setShowThresholdModal(true)}
              // onViewStats={() => setShowStatsModal(true)}
            />
          )}
          {activeTab === 'monitoring' && (
            <MonitoringTab 
              userProfile={userProfile} 
              onAddMatch={() => setShowAddMatchModal(true)}
              onConfigureThresholds={() => setShowThresholdModal(true)}
              onEditMatch={(match) => {
                setEditingMatch(match);
                setShowThresholdModal(true);
              }}
              onRemoveMatch={handleRemoveMatch}
              updateUserProfile={updateUserProfile}
              currentUser={currentUser}
            />
          )}
          {activeTab === 'tickets' && (
            <TicketsTab 
              userProfile={userProfile}
            />
          )}
          {activeTab === 'alerts' && <AlertsTab userProfile={userProfile} />}
          {activeTab === 'settings' && <SettingsTab userProfile={userProfile} currentUser={currentUser} />}
        </div>
      </main>

      {/* Modales */}
      {showAddMatchModal && (
        <AddMatchModal 
          onClose={() => setShowAddMatchModal(false)}
          onAddMatch={handleAddMatch}
          userProfile={userProfile}
          updateUserProfile={updateUserProfile}
        />
      )}
      
      {showThresholdModal && (
        <ThresholdModal 
          onClose={() => {
            setShowThresholdModal(false);
            setEditingMatch(null);
          }}
          userProfile={userProfile}
          editingMatch={editingMatch}
          updateUserProfile={updateUserProfile}
        />
      )}
      
      {/* {showStatsModal && (
        <StatsModal 
          onClose={() => setShowStatsModal(false)}
          userProfile={userProfile}
        />
      )} */}

      {/* Footer Legal */}
      <footer className="legal-footer">
        <div className="legal-content">
          <div className="legal-links">
            <a href="/terms.html" target="_blank" rel="noopener noreferrer">Términos de Servicio</a>
            <span>•</span>
            <a href="/privacy.html" target="_blank" rel="noopener noreferrer">Política de Privacidad</a>
            <span>•</span>
            <a href="/disclaimer.html" target="_blank" rel="noopener noreferrer">Disclaimer Legal</a>
          </div>
          <div className="legal-copyright">
            <p>© 2025 RTB Collect. Todos los derechos reservados.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

// Componente de Resumen
function OverviewTab({ userProfile, onAddMatch, onConfigureAlerts /*, onViewStats*/ }) {
  return (
    <div className="tab-content">
      <div className="tab-header">
        <h2>Resumen General</h2>
        <p>Vista general de tu monitoreo de precios</p>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">🏟️</div>
          <div className="stat-content">
            <h3>Partidos Monitoreados</h3>
            <p className="stat-value">{userProfile?.configuracion?.partidos?.length || 0}</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">🔔</div>
          <div className="stat-content">
            <h3>Alertas Recibidas</h3>
            <p className="stat-value">{userProfile?.totalAlertas || 0}</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">⚡</div>
          <div className="stat-content">
            <h3>Frecuencia de Consulta</h3>
            <p className="stat-value">{userProfile?.configuracion?.frecuencia || 60} min</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">💎</div>
          <div className="stat-content">
            <h3>Plan Actual</h3>
            <p className="stat-value">{userProfile?.plan || 'Gratuito'}</p>
          </div>
        </div>
      </div>

      {/* Tabla de Tickets */}
      <TicketsTable userProfile={userProfile} />

      <div className="quick-actions">
        <h3>Acciones Rápidas</h3>
        <div className="actions-grid">
          <button className="action-btn" onClick={onAddMatch}>
            <Plus size={20} />
            Agregar Partido
          </button>
          <button className="action-btn" onClick={onConfigureAlerts}>
            <Settings size={20} />
            Configurar Alertas
          </button>
          {/* <button className="action-btn" onClick={onViewStats}>
            <BarChart3 size={20} />
            Ver Estadísticas
          </button> */}
        </div>
      </div>
    </div>
  );
}

// Componente de Monitoreo
function MonitoringTab({ userProfile, onAddMatch, onConfigureThresholds, onEditMatch, onRemoveMatch, updateUserProfile, currentUser }) {
  const [availableMatches, setAvailableMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFrequencyModal, setShowFrequencyModal] = useState(false);

  // Debug: Log userProfile para ver qué datos llegan
  useEffect(() => {
    console.log('🔍 MonitoringTab - userProfile recibido:', {
      plan: userProfile?.plan,
      frecuencia: userProfile?.configuracion?.frecuencia,
      partidos: userProfile?.configuracion?.partidos?.length,
      partidosData: userProfile?.configuracion?.partidos,
      onboardingCompletado: userProfile?.onboardingCompletado
    });
  }, [userProfile]);

  // Cargar partidos disponibles desde la API
  useEffect(() => {
    const fetchMatches = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const matches = await apiRequest('/api/tickets/matches');
        setAvailableMatches(matches);
      } catch (err) {
        console.error('Error cargando partidos:', err);
        setError('Error al cargar los partidos disponibles');
      } finally {
        setLoading(false);
      }
    };

    fetchMatches();
  }, []);

  // Obtener información actualizada de los partidos monitoreados
  const getMonitoredMatchesInfo = () => {
    if (!userProfile?.configuracion?.partidos || !availableMatches.length) {
      return [];
    }

    return userProfile.configuracion.partidos.map(partidoConfig => {
      const matchInfo = availableMatches.find(match => match.matchNo === partidoConfig.matchNo);
      return {
        ...partidoConfig,
        ...matchInfo,
        precioActual: matchInfo?.floorPrice || 0,
        diferencia: matchInfo ? partidoConfig.umbral - matchInfo.floorPrice : 0
      };
    });
  };

  const monitoredMatches = getMonitoredMatchesInfo();

  if (loading) {
    return (
      <div className="tab-content">
        <div className="tab-header">
          <h2>Configuración de Monitoreo</h2>
          <p>Gestiona los partidos que quieres monitorear</p>
        </div>
        
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Cargando partidos monitoreados...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tab-content">
        <div className="tab-header">
          <h2>Configuración de Monitoreo</h2>
          <p>Gestiona los partidos que quieres monitorear</p>
        </div>
        
        <div className="error-state">
          <div className="error-icon">⚠️</div>
          <h3>Error al cargar datos</h3>
          <p>{error}</p>
          <button 
            className="btn btn-primary"
            onClick={() => window.location.reload()}
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="monitoring-section">
      <div className="section-header">
        <h3>Partidos Monitoreados ({monitoredMatches.length})</h3>
        <div className="header-actions">
        <button className="btn btn-primary" onClick={onAddMatch}>
            <Plus size={18} />
            Agregar Partido
          </button>
          <button className="btn btn-secondary" onClick={onConfigureThresholds}>
            <Settings size={18} />
            Configurar Umbrales
          </button>
          <button 
            className="btn btn-secondary" 
            onClick={() => setShowFrequencyModal(true)}
            title={`Frecuencia actual: ${userProfile?.configuracion?.frecuencia || 60} min`}
          >
            <Clock size={18} />
            Frecuencia ({userProfile?.configuracion?.frecuencia || 60} min)
          </button>
          <button 
            className="btn btn-secondary" 
            onClick={async () => {
              console.log('🔄 Refrescando perfil manualmente...');
              await updateUserProfile(currentUser?.uid);
            }}
            title="Refrescar datos del perfil"
          >
            🔄 Refrescar
          </button>
          <div className="scroll-indicator-mobile">
            <span></span>
          </div>
        </div>
      </div>

      {monitoredMatches.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">⚽</div>
          <h3>No tienes partidos monitoreados</h3>
          <p>Agrega partidos a tu lista de monitoreo para recibir alertas cuando los precios bajen.</p>
          <button className="btn btn-primary" onClick={onAddMatch}>
            <Plus size={18} />
            Agregar Primer Partido
          </button>
        </div>
      ) : (
        <div className="matches-grid">
          {monitoredMatches.map((match, index) => (
            <div key={match.matchNo} className="match-card-modern">
              {/* Header con estado y número de partido */}
              <div className="match-header-modern">
                <div className="match-badge-modern">
                  <span className="match-number-modern">M{match.matchNo}</span>
                </div>
                <div className={`status-indicator-modern ${match.diferencia > 0 ? 'alert' : 'normal'}`}>
                  {match.diferencia > 0 ? (
                    <>
                      <div className="pulse-dot"></div>
                      <span>ALERTA</span>
                    </>
                  ) : (
                    <>
                      <div className="normal-dot"></div>
                      <span>NORMAL</span>
                    </>
                  )}
                </div>
              </div>

              {/* Equipos y competición */}
              <div className="match-teams-modern">
                <h3 className="teams-title">{match.teams}</h3>
                <div className="match-competition">
                  <span className="round-info">{match.roundInfo}</span>
                </div>
              </div>
              
              {/* Información del estadio y fecha */}
              <div className="match-venue-info">
                <div className="venue-item">
                  <div className="venue-icon">🏟️</div>
                  <div className="venue-details">
                    <div className="venue-name">{match.stadium}</div>
                    <div className="venue-date">{new Date(match.date).toLocaleDateString('es-ES', { 
                      day: 'numeric', 
                      month: 'short', 
                      year: 'numeric' 
                    })}</div>
                  </div>
                </div>
              </div>

              {/* Información de precios mejorada */}
              <div className="price-section-modern">
                <div className="price-card current-price-card">
                  <div className="price-header">
                    <span className="price-label-modern">Precio Actual</span>
                    <div className="price-trend">
                      {match.diferencia > 0 ? '📉' : '📊'}
                    </div>
                  </div>
                  <div className="price-value-modern current">
                    ${match.precioActual?.toLocaleString() || 'N/A'}
                  </div>
                </div>
                
                <div className="vs-divider">VS</div>
                
                <div className="price-card threshold-price-card">
                  <div className="price-header">
                    <span className="price-label-modern">Tu Umbral</span>
                    <div className="target-icon">🎯</div>
                  </div>
                  <div className="price-value-modern threshold">
                    ${match.umbral?.toLocaleString() || 'N/A'}
                  </div>
                </div>
              </div>

              {/* Diferencia de precio */}
              <div className="price-difference-modern">
                {match.precioActual && match.umbral && (
                  <div className={`difference-indicator ${match.diferencia > 0 ? 'positive' : 'negative'}`}>
                    {match.diferencia > 0 ? (
                      <>
                        <span className="diff-icon">💰</span>
                        <span>¡${match.diferencia.toLocaleString()} por debajo!</span>
                      </>
                    ) : (
                      <>
                        <span className="diff-icon">⏳</span>
                        <span>${Math.abs(match.diferencia).toLocaleString()} por encima</span>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Acciones mejoradas */}
              <div className="match-actions-modern">
                <button
                  className="action-btn-modern edit-btn"
                  onClick={() => onEditMatch(match)}
                  title="Editar umbral"
                >
                  <Edit size={16} />
                  <span>Editar</span>
                </button>
                
                <button
                  className="action-btn-modern buy-btn-modern"
                  onClick={() => window.open(`https://collect.fifa.com/marketplace?tags=rtt-m${match.matchNo}`, '_blank')}
                  title="Ver en Marketplace"
                >
                  <ExternalLink size={16} />
                  <span>Comprar</span>
                </button>
                
                <button
                  className="action-btn-modern remove-btn"
                  onClick={() => onRemoveMatch(match.matchNo)}
                  title="Remover del monitoreo"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="monitoring-stats">
        <h3>Estadísticas de Monitoreo</h3>
        <div className="stats-grid">
          <div className="stat-item">
            <span className="stat-label">Partidos Monitoreados</span>
            <span className="stat-value">{monitoredMatches.length}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Alertas Activas</span>
            <span className="stat-value alert">
              {monitoredMatches.filter(m => m.diferencia > 0).length}
            </span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Frecuencia de Monitoreo</span>
            <span className="stat-value">
              {userProfile?.configuracion?.frecuencia || 60} min
            </span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Plan Actual</span>
            <span className="stat-value plan">
              {userProfile?.plan || 'Básico'}
            </span>
          </div>
        </div>
      </div>

      {/* Modal de Configuración de Frecuencia */}
      {showFrequencyModal && (
        <FrequencyModal
          userProfile={userProfile}
          currentUser={currentUser}
          onClose={() => setShowFrequencyModal(false)}
          updateUserProfile={updateUserProfile}
        />
      )}
    </div>
  );
}

// Componente de Alertas
function AlertsTab({ userProfile }) {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAlerts = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await apiRequest('/api/users/alerts', {
          params: { limit: 50, offset: 0 }
        });
        
        setAlerts(response.alertas || []);
      } catch (error) {
        console.error('Error cargando alertas:', error);
        setError('Error al cargar alertas');
        toast.error('Error al cargar historial de alertas');
      } finally {
        setLoading(false);
      }
    };

    fetchAlerts();
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="tab-content">
        <div className="tab-header">
          <h2>Historial de Alertas</h2>
          <p>Revisa las alertas de precio que has recibido</p>
        </div>
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Cargando alertas...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tab-content">
        <div className="tab-header">
          <h2>Historial de Alertas</h2>
          <p>Revisa las alertas de precio que has recibido</p>
        </div>
        <div className="error-state">
          <div className="error-icon">⚠️</div>
          <h3>Error al cargar alertas</h3>
          <p>{error}</p>
          <button className="btn btn-primary" onClick={() => window.location.reload()}>
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="tab-content">
      <div className="tab-header">
        <h2>Historial de Alertas</h2>
        <p>Revisa las alertas de precio que has recibido</p>
      </div>

      <div className="alerts-section">
        {alerts.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🔔</div>
            <h3>No hay alertas aún</h3>
            <p>Las alertas aparecerán aquí cuando los precios bajen de tus umbrales configurados</p>
          </div>
        ) : (
          <div className="alerts-list">
            {alerts.map((alert) => (
              <div 
                key={alert.id} 
                className="alert-card"
                onClick={() => window.open(`https://collect.fifa.com/marketplace?tags=rtt-m${alert.matchNo}`, '_blank')}
              >
                <div className="alert-header">
                  <h3>Partido {alert.matchNo}</h3>
                  <span className="alert-date">{formatDate(alert.fecha)}</span>
                </div>
                <div className="alert-content">
                  <div className="alert-info">
                    <p><strong>Equipos:</strong> {alert.partido_info?.teams || 'N/A'}</p>
                    <p><strong>Ronda:</strong> {alert.partido_info?.roundInfo || 'N/A'}</p>
                    <p><strong>Estadio:</strong> {alert.partido_info?.stadium || 'N/A'}</p>
                  </div>
                  <div className="alert-prices">
                    <div className="price-item">
                      <span className="price-label">Precio actual:</span>
                      <span className="price-value current">${alert.precio_actual?.toLocaleString() || 'N/A'}</span>
                    </div>
                    <div className="price-item">
                      <span className="price-label">Umbral configurado:</span>
                      <span className="price-value threshold">${alert.umbral?.toLocaleString() || 'N/A'}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Componente de Configuración
function SettingsTab({ userProfile, currentUser }) {
  const [loading, setLoading] = useState(false);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [activeSection, setActiveSection] = useState('profile');

  const handlePlanUpdate = async (newPlan) => {
    const currentPlan = userProfile?.plan || 'basico';
    
    // Definir jerarquía de planes
    const planHierarchy = { 'basico': 1, 'premium': 2, 'elite': 3 };
    const planPrices = { 'basico': 100, 'premium': 200, 'elite': 400 };
    
    // Verificar si es un downgrade (no permitido)
    if (planHierarchy[newPlan] < planHierarchy[currentPlan]) {
      toast.error('No puedes cambiar a un plan de menor categoría');
      return;
    }
    
    // Si es el mismo plan
    if (newPlan === currentPlan) {
      toast.info('Ya tienes este plan activo');
      return;
    }
    
    // Si es un upgrade, calcular diferencia y proceder al pago
    if (planHierarchy[newPlan] > planHierarchy[currentPlan]) {
      const currentPrice = planPrices[currentPlan];
      const newPrice = planPrices[newPlan];
      const priceDifference = newPrice - currentPrice;
      
      try {
        setLoading(true);
        
        // Obtener userId correctamente
        const userId = userProfile?.uid || userProfile?.id || currentUser?.uid;
        
        console.log('🔍 Debug upgrade:', {
          userId,
          currentPlan,
          newPlan,
          priceDifference,
          userProfile,
          currentUser
        });
        
        if (!userId) {
          toast.error('Error: Usuario no identificado');
          return;
        }
        
        // Crear sesión de Stripe para el upgrade
        const response = await apiRequest('/api/payments/create-upgrade-session', {
          method: 'POST',
          body: {
            userId: userId,
            currentPlan: currentPlan,
            newPlan: newPlan,
            upgradeCost: priceDifference
          }
        });
        
        if (response.url) {
          // Redirigir a Stripe para pagar la diferencia
          window.location.href = response.url;
        } else {
          toast.error('Error al crear sesión de pago');
        }
      } catch (error) {
        console.error('Error creando sesión de upgrade:', error);
        toast.error('Error al procesar el upgrade');
      } finally {
        setLoading(false);
      }
    }
  };

  const getPlanFeatures = (plan) => {
    const features = {
      basico: {
        name: 'Plan Básico',
        price: '$100 MXN',
        matches: '1 partido',
        frequency: 'Monitoreo cada hora',
        details: ['1 partido', 'Monitoreo cada hora', 'Alertas por email'],
        color: '#6b7280',
        gradient: 'linear-gradient(135deg, #6b7280 0%, #9ca3af 100%)',
        icon: '⭐'
      },
      premium: {
        name: 'Plan Premium',
        price: '$200 MXN',
        matches: 'Hasta 3 partidos',
        frequency: 'Monitoreo cada 10 min',
        details: ['Hasta 3 partidos', 'Monitoreo cada 10 min', 'Alertas por email', 'Dashboard avanzado'],
        color: '#3b82f6',
        gradient: 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)',
        icon: '💎'
      },
      elite: {
        name: 'Plan Elite',
        price: '$400 MXN',
        matches: 'Hasta 7 partidos',
        frequency: 'Monitoreo cada 5 min',
        details: ['Hasta 7 partidos', 'Monitoreo cada 5 min', 'Alertas por email', 'Dashboard completo', 'Soporte prioritario'],
        color: '#8b5cf6',
        gradient: 'linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%)',
        icon: '👑'
      }
    };
    return features[plan] || features.basico;
  };

  const currentPlan = getPlanFeatures(userProfile?.plan);
  const availablePlans = ['basico', 'premium', 'elite'];
  const totalPlans = availablePlans.length;

  const nextPlan = () => setCarouselIndex((prev) => (prev + 1) % totalPlans);
  const prevPlan = () => setCarouselIndex((prev) => (prev - 1 + totalPlans) % totalPlans);

  return (
    <div className="tab-content">
      {/* Contenido de secciones */}
      <div className="settings-content">
        <div className="tab-header">
          <h2>Configuración de Cuenta</h2>
          <p>Gestiona tu cuenta y preferencias</p>
        </div>

        {/* Navegación de secciones */}
        <div className="settings-nav">
          <button 
            className={`settings-nav-btn ${activeSection === 'profile' ? 'active' : ''}`}
            onClick={() => setActiveSection('profile')}
          >
            <div className="nav-icon">👤</div>
            <span>Perfil</span>
          </button>
          <button 
            className={`settings-nav-btn ${activeSection === 'plan' ? 'active' : ''}`}
            onClick={() => setActiveSection('plan')}
          >
            <div className="nav-icon">💎</div>
            <span>Plan Actual</span>
          </button>
          <button 
            className={`settings-nav-btn ${activeSection === 'upgrade' ? 'active' : ''}`}
            onClick={() => setActiveSection('upgrade')}
          >
            <div className="nav-icon">🚀</div>
            <span>Cambiar Plan</span>
          </button>
        </div>

        {activeSection === 'profile' && (
          <div className="profile-section">
            <div className="profile-header">
              <div className="profile-avatar">
                <span>{userProfile?.username?.charAt(0)?.toUpperCase() || 'U'}</span>
              </div>
              <div className="profile-info">
                <h3>{userProfile?.username || 'Usuario'}</h3>
                <p className="profile-email">{userProfile?.email || 'No configurado'}</p>
                <div className="profile-status">
                  <span className="status-dot active"></span>
                  <span>{userProfile?.onboardingCompletado ? 'Cuenta Activa' : 'Configuración Pendiente'}</span>
                </div>
              </div>
            </div>

            <div className="profile-details">
              <div className="detail-card">
                <div className="detail-icon">📅</div>
                <div className="detail-content">
                  <label>Miembro desde</label>
                  <p>{(() => {
                    try {
                      if (!userProfile?.createdAt) {
                        return 'Fecha no disponible';
                      }
                      
                      // Si es un timestamp de Firebase (objeto con seconds)
                      if (userProfile.createdAt.seconds) {
                        const date = new Date(userProfile.createdAt.seconds * 1000);
                        return date.toLocaleDateString('es-ES', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        });
                      }
                      
                      // Si es un string de fecha
                      if (typeof userProfile.createdAt === 'string') {
                        const date = new Date(userProfile.createdAt);
                        if (!isNaN(date.getTime())) {
                          return date.toLocaleDateString('es-ES', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          });
                        }
                      }
                      
                      // Si es una fecha normal
                      const date = new Date(userProfile.createdAt);
                      if (!isNaN(date.getTime())) {
                        return date.toLocaleDateString('es-ES', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        });
                      }
                      
                      return 'Fecha no disponible';
                    } catch (error) {
                      console.error('Error formateando fecha:', error);
                      return 'Fecha no disponible';
                    }
                  })()}</p>
                </div>
              </div>

              <div className="detail-card">
                <div className="detail-icon">🎯</div>
                <div className="detail-content">
                  <label>Partidos Monitoreados</label>
                  <p>{userProfile?.configuracion?.partidos?.length || 0} partidos</p>
                </div>
              </div>

              <div className="detail-card">
                <div className="detail-icon">⚡</div>
                <div className="detail-content">
                  <label>Frecuencia de Monitoreo</label>
                  <p>Cada {userProfile?.configuracion?.frecuencia || 60} minutos</p>
                </div>
              </div>

              <div className="detail-card">
                <div className="detail-icon">🔔</div>
                <div className="detail-content">
                  <label>Alertas Recibidas</label>
                  <p>{userProfile?.totalAlertas || 0} alertas</p>
                </div>
              </div>

              {/* Card de Bluee Code Labs */}
              <div 
                className="detail-card bluee-code-card"
                onClick={() => window.open('https://blueecodelabs.com', '_blank')}
                style={{ cursor: 'pointer' }}
                title="Visitar Bluee Code Labs"
              >
                
                  <img 
                    src="/profilepicture_blanca_sinfondo.png" 
                    alt="Bluee Code Labs Logo" 
                    style={{ width: '50px', height: '50px', objectFit: 'contain' }}
                  />
                
                <div className="detail-content">
                  <label>Desarrollado por</label>
                  <p>Bluee Code Labs</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeSection === 'plan' && (
          <div className="current-plan-section">
            <div className="plan-card current">
              <div className="plan-header">
                <div className="plan-icon">
                  <span>{currentPlan.icon}</span>
                </div>
                <div className="plan-info">
                  <h3>{currentPlan.name}</h3>
                  <p className="plan-description">Tu plan actual</p>
                </div>
                <div className="plan-status">
                  <span className="status-badge active">Activo</span>
                </div>
              </div>

              <div className="plan-price-section">
                <div className="price-display">
                  <span className="price-amount">{currentPlan.price}</span>
                </div>
              </div>

              <div className="plan-features-list">
                {currentPlan.details.map((detail, index) => (
                  <div key={index} className="feature-item">
                    <div className="feature-icon">
                      {index === 0 ? '🎯' : index === 1 ? '⚡' : index === 2 ? '🔔' : index === 3 ? '📊' : '🏆'}
                    </div>
                    <span>{detail}</span>
                  </div>
                ))}
              </div>

              <div className="plan-usage">
                <div className="usage-item">
                  <label>Uso de partidos</label>
                  <div className="usage-bar">
                    <div 
                      className="usage-fill" 
                      style={{ 
                        width: `${Math.min((userProfile?.configuracion?.partidos?.length || 0) / (userProfile?.plan === 'basico' ? 1 : userProfile?.plan === 'premium' ? 3 : 7) * 100, 100)}%`,
                        background: 'var(--gradient-accent)'
                      }}
                    ></div>
                  </div>
                  <div className="usage-text">
                    {userProfile?.configuracion?.partidos?.length || 0} de {userProfile?.plan === 'basico' ? 1 : userProfile?.plan === 'premium' ? 3 : 7} partidos
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeSection === 'upgrade' && (
          <div className="upgrade-section">
            <div className="upgrade-header">
              <h3>Mejora tu Plan</h3>
              <p>Desbloquea más funcionalidades y monitorea más partidos</p>
            </div>

            <div className="plans-carousel">
              <button className="carousel-nav" onClick={prevPlan}>
                <span>‹</span>
              </button>
              
              <div className="carousel-viewport">
                <div className="carousel-track" style={{ transform: `translateX(-${carouselIndex * 100}%)` }}>
                  {availablePlans.map((plan, index) => {
                    const planFeatures = getPlanFeatures(plan);
                    const isCurrentPlan = plan === userProfile?.plan;
                    const currentPlan = userProfile?.plan || 'basico';
                    
                    // Definir jerarquía y precios
                    const planHierarchy = { 'basico': 1, 'premium': 2, 'elite': 3 };
                    const planPrices = { 'basico': 100, 'premium': 200, 'elite': 400 };
                    
                    const isDowngrade = planHierarchy[plan] < planHierarchy[currentPlan];
                    const isUpgrade = planHierarchy[plan] > planHierarchy[currentPlan];
                    const upgradeCost = isUpgrade ? planPrices[plan] - planPrices[currentPlan] : 0;
                    
                    return (
                      <div key={plan} className="carousel-slide">
                        <div className={`plan-option ${isDowngrade ? 'disabled-plan' : ''}`}>
                          {isCurrentPlan && (
                            <div className="current-plan-indicator">Actual</div>
                          )}
                          {isDowngrade && (
                            <div className="downgrade-indicator">No disponible</div>
                          )}
                          
                          <div className="plan-option-header">
                            <div className="plan-option-icon">
                              <span>{planFeatures.icon}</span>
                            </div>
                            <div className="plan-option-info">
                              <div className="plan-option-name">{planFeatures.name}</div>
                              <div className="plan-option-price">
                                {isUpgrade ? (
                                  <div>
                                    <span className="upgrade-price">${upgradeCost.toLocaleString()} MXN</span>
                                    <small className="upgrade-note">Solo pagas la diferencia</small>
                                  </div>
                                ) : (
                                  planFeatures.price
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="plan-option-features">
                            {planFeatures.details.map((detail, index) => (
                              <div key={index} className="feature-item">
                                <div className="feature-check">✓</div>
                                <span>{detail}</span>
                              </div>
                            ))}
                          </div>

                          {isCurrentPlan ? (
                            <button className="current-plan-btn" disabled>
                              Plan Actual
                            </button>
                          ) : isDowngrade ? (
                            <button className="downgrade-btn" disabled>
                              No Disponible
                            </button>
                          ) : (
                            <button 
                              className="plan-select-btn"
                              onClick={() => handlePlanUpdate(plan)}
                              disabled={loading}
                            >
                              {loading ? 'Procesando...' : isUpgrade ? `Actualizar por $${upgradeCost.toLocaleString()}` : 'Seleccionar Plan'}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <button className="carousel-nav" onClick={nextPlan}>
                <span>›</span>
              </button>
            </div>

            <div className="carousel-dots">
              {availablePlans.map((_, index) => (
                <button
                  key={index}
                  className={`dot ${index === carouselIndex ? 'active' : ''}`}
                  onClick={() => setCarouselIndex(index)}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Modal para agregar partidos
function AddMatchModal({ onClose, onAddMatch, userProfile }) {
  const [availableMatches, setAvailableMatches] = useState([]);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [threshold, setThreshold] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchMatches = async () => {
      try {
        const matches = await apiRequest('/api/tickets/matches');
        const currentMatchNos = userProfile?.configuracion?.partidos?.map(p => p.matchNo) || [];
        const filtered = matches.filter(match => !currentMatchNos.includes(match.matchNo));
        setAvailableMatches(filtered);
      } catch (error) {
        console.error('Error cargando partidos:', error);
        toast.error('Error al cargar partidos disponibles');
      }
    };

    fetchMatches();
  }, [userProfile]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedMatch || !threshold || threshold <= 0) {
      toast.error('Debes seleccionar un partido y configurar un umbral válido');
      return;
    }

    setLoading(true);
    try {
      const matchData = {
        matchNo: selectedMatch.matchNo,
        teams: selectedMatch.teams,
        roundInfo: selectedMatch.roundInfo,
        umbral: parseFloat(threshold)
      };

      await onAddMatch(matchData);
    } catch (error) {
      console.error('Error agregando partido:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Agregar Partido</h2>
          <button className="modal-close" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-body">
          <div className="form-group">
            <label>Seleccionar Partido</label>
            <div className="matches-selector">
              {availableMatches.map((match) => (
                <div 
                  key={match.matchNo}
                  className={`match-option ${selectedMatch?.matchNo === match.matchNo ? 'selected' : ''}`}
                  onClick={() => setSelectedMatch(match)}
                >
                  <div className="match-info">
                    <h4>M{match.matchNo} - {match.teams}</h4>
                    <p>{match.roundInfo}</p>
                    <p>Precio actual: ${match.floorPrice?.toLocaleString()}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {selectedMatch && (
            <div className="form-group">
              <label>
                Umbral de alerta ($) <span className="required">*</span>
              </label>
              <input
                type="number"
                value={threshold}
                onChange={(e) => setThreshold(e.target.value)}
                placeholder="Ej: 600"
                min="0"
                step="0.01"
                required
              />
            </div>
          )}

          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancelar
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading || !selectedMatch}>
              {loading ? 'Agregando...' : 'Agregar Partido'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Modal para configurar umbrales
function ThresholdModal({ onClose, userProfile, editingMatch, updateUserProfile }) {
  const { currentUser } = useAuth();
  const [thresholds, setThresholds] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (editingMatch) {
      // Si estamos editando un partido específico
      setThresholds({ [editingMatch.matchNo]: editingMatch.umbral || '' });
    } else {
      // Si estamos configurando todos los umbrales
      const initialThresholds = {};
      userProfile?.configuracion?.partidos?.forEach(partido => {
        initialThresholds[partido.matchNo] = partido.umbral || '';
      });
      setThresholds(initialThresholds);
    }
  }, [userProfile, editingMatch]);

  const handleThresholdChange = (matchNo, value) => {
    setThresholds({
      ...thresholds,
      [matchNo]: parseFloat(value) || 0
    });
  };

  const handleSaveThresholds = async (thresholds) => {
    try {
      setLoading(true);
      
      const newConfig = {
        ...userProfile.configuracion,
        partidos: userProfile.configuracion.partidos.map(partido => ({
          ...partido,
          umbral: thresholds[partido.matchNo] || partido.umbral
        }))
      };

      const requestBody = {
        partidos: newConfig.partidos,
        umbrales: thresholds,
        frecuencia: newConfig.frecuencia || 10
      };

      console.log('📤 Enviando datos al backend:', JSON.stringify(requestBody, null, 2));

      // Actualizar en backend
      const response = await apiRequest('/api/users/config', {
        method: 'PUT',
        body: requestBody
      });

      if (response.success) {
        // Actualizar el contexto
        await updateUserProfile(currentUser.uid);
        toast.success('Umbrales actualizados exitosamente');
        onClose(); // Usar onClose en lugar de setShowThresholdModal
      }
    } catch (error) {
      console.error('Error guardando umbrales:', error);
      console.error('📋 Detalles del error:', error.message);
      toast.error('Error al guardar umbrales');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validar que todos los umbrales estén configurados
    const hasValidThresholds = Object.values(thresholds).every(value => value > 0);
    if (!hasValidThresholds) {
      toast.error('Debes configurar umbrales válidos para todos los partidos');
      return;
    }

    await handleSaveThresholds(thresholds);
  };

  const matchesToEdit = editingMatch ? [editingMatch] : userProfile?.configuracion?.partidos || [];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{editingMatch ? 'Editar Umbral' : 'Configurar Umbrales'}</h2>
          <button className="modal-close" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-body">
          <div className="thresholds-config">
            {matchesToEdit.map((match) => (
              <div key={match.matchNo} className="threshold-item">
                <div className="match-info">
                  <h4>M{match.matchNo} - {match.teams}</h4>
                  <p>{match.roundInfo}</p>
                </div>
                <div className="threshold-input">
                  <label>
                    Umbral de alerta ($) <span className="required">*</span>
                  </label>
                  <input
                    type="number"
                    value={thresholds[match.matchNo] || ''}
                    onChange={(e) => handleThresholdChange(match.matchNo, e.target.value)}
                    placeholder="Ej: 600"
                    min="0"
                    step="0.01"
                    required
                  />
                </div>
              </div>
            ))}
          </div>

          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancelar
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Guardando...' : 'Guardar Umbrales'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Modal para estadísticas - COMENTADO: No se usa actualmente
/*
function StatsModal({ onClose, userProfile }) {
  // Calcular estadísticas del usuario
  const partidosMonitoreados = userProfile?.configuracion?.partidos?.length || 0;
  const alertasRecibidas = userProfile?.totalAlertas || 0;
  const planActual = userProfile?.plan || 'basico';
  const frecuenciaMonitoreo = userProfile?.configuracion?.frecuencia || 60;
  const estadoMonitoreo = userProfile?.configuracion?.activo ? 'Activo' : 'Inactivo';
  
  // Debug para alertas
  console.log('🔍 Debug alertas:', {
    totalAlertas: userProfile?.totalAlertas,
    alertasRecibidas,
    userProfile: userProfile
  });
  
  // Calcular tiempo desde que es miembro
  const fechaCreacion = userProfile?.createdAt;
  let tiempoMiembro = 'No disponible';
  
  if (fechaCreacion) {
    try {
      let fechaDate;
      
      // Si es un timestamp de Firebase (objeto con seconds)
      if (fechaCreacion.seconds) {
        fechaDate = new Date(fechaCreacion.seconds * 1000);
      } 
      // Si es un string de fecha
      else if (typeof fechaCreacion === 'string') {
        fechaDate = new Date(fechaCreacion);
      }
      // Si es un objeto Date
      else {
        fechaDate = new Date(fechaCreacion);
      }
      
      // Verificar que la fecha es válida
      if (isNaN(fechaDate.getTime())) {
        tiempoMiembro = 'Fecha inválida';
      } else {
        const ahora = new Date();
        const diferenciaDias = Math.floor((ahora - fechaDate) / (1000 * 60 * 60 * 24));
        
        if (diferenciaDias < 1) {
          tiempoMiembro = 'Menos de 1 día';
        } else if (diferenciaDias < 30) {
          tiempoMiembro = `${diferenciaDias} ${diferenciaDias === 1 ? 'día' : 'días'}`;
        } else if (diferenciaDias < 365) {
          const meses = Math.floor(diferenciaDias / 30);
          tiempoMiembro = `${meses} ${meses === 1 ? 'mes' : 'meses'}`;
        } else {
          const años = Math.floor(diferenciaDias / 365);
          tiempoMiembro = `${años} ${años === 1 ? 'año' : 'años'}`;
        }
      }
    } catch (error) {
      console.error('Error calculando tiempo como miembro:', error);
      tiempoMiembro = 'Error al calcular';
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content stats-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Mis Estadísticas</h2>
          <button className="modal-close" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <div className="modal-body">
          <div className="stats-content">
            <div className="stats-section">
              <h3>📊 Resumen de Actividad</h3>
              <div className="stats-grid">
                <div className="stat-item highlight">
                  <span className="stat-label">🏟️ Partidos Siguiendo</span>
                  <span className="stat-value">{partidosMonitoreados}</span>
                </div>
                <div className="stat-item highlight">
                  <span className="stat-label">🔔 Alertas Recibidas</span>
                  <span className="stat-value">{alertasRecibidas}</span>
                </div>
              </div>
            </div>

            <div className="stats-section">
              <h3>⚙️ Configuración Actual</h3>
              <div className="stats-grid">
                <div className="stat-item">
                  <span className="stat-label">💎 Plan Actual</span>
                  <span className="stat-value plan-badge">{planActual}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">⏱️ Frecuencia de Monitoreo</span>
                  <span className="stat-value">{frecuenciaMonitoreo} min</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">📡 Estado del Monitoreo</span>
                  <span className={`stat-value status-badge ${estadoMonitoreo.toLowerCase()}`}>
                    {estadoMonitoreo}
                  </span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">📅 Miembro desde hace</span>
                  <span className="stat-value">{tiempoMiembro}</span>
                </div>
              </div>
            </div>

            <div className="stats-section">
              <h3>🎯 Límites del Plan</h3>
              <div className="stats-grid">
                <div className="stat-item">
                  <span className="stat-label">Partidos Máximos</span>
                  <span className="stat-value">
                    {planActual === 'basico' ? '1' : planActual === 'premium' ? '3' : '7'}
                  </span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Frecuencia Mínima</span>
                  <span className="stat-value">
                    {planActual === 'basico' ? '60' : planActual === 'premium' ? '10' : '5'} min
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="modal-actions">
            <button 
              type="button" 
              className="btn btn-secondary"
              onClick={async () => {
                try {
                  await apiRequest('/api/users/create-test-alert', {
                    method: 'POST',
                    body: JSON.stringify({
                      matchNo: 1,
                      price: 500,
                      message: 'Alerta de prueba - precio detectado'
                    })
                  });
                  alert('Alerta de prueba creada. Recarga la página para ver el cambio.');
                } catch (error) {
                  console.error('Error creando alerta de prueba:', error);
                  alert('Error creando alerta de prueba');
                }
              }}
            >
              🧪 Crear Alerta de Prueba
            </button>
            <button type="button" className="btn btn-primary" onClick={onClose}>
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
*/

// Componente de tabla de partidos
// Componente de tabla de Tickets (reutilizable)
function TicketsTable({ userProfile }) {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTickets = async () => {
      try {
        setLoading(true);
        setError(null);
        
        console.log('🔍 Consultando /api/tickets/matches...');
        const response = await apiRequest('/api/tickets/matches');
        
        console.log('📦 Respuesta recibida:', response);
        
        if (Array.isArray(response)) {
          console.log(`✅ ${response.length} tickets cargados`);
          setTickets(response);
        } else {
          console.error('❌ Estructura de respuesta inesperada:', response);
          setError(`No se pudieron cargar los tickets. Respuesta: ${JSON.stringify(response)}`);
        }
      } catch (err) {
        console.error('❌ Error cargando tickets:', err);
        setError(`Error al cargar los tickets: ${err.message || err.toString()}`);
      } finally {
        setLoading(false);
      }
    };

    fetchTickets();
  }, []);

  if (loading) {
    return (
      <div className="matches-table-container">
        <h3>🎫 Tickets Disponibles</h3>
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Cargando tickets...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="matches-table-container">
        <h3>🎫 Tickets Disponibles</h3>
        <div className="error-state">
          <div className="error-icon">⚠️</div>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (!tickets.length) {
    return (
      <div className="matches-table-container">
        <h3>🎫 Tickets Disponibles</h3>
        <div className="empty-state">
          <div className="empty-icon">🎫</div>
          <p>No hay tickets disponibles en este momento</p>
        </div>
      </div>
    );
  }

  return (
    <div className="matches-table-container">
      <h3>🎫 Tickets Disponibles ({tickets.length} partidos)</h3>
      <div className="matches-table-wrapper">
        <div className="scroll-indicator">
          <span>📊 {tickets.length} partidos disponibles</span>
          <span>Desliza horizontal | Scroll vertical</span>
        </div>
        <table className="matches-table">
          <thead>
            <tr>
              <th>Partido</th>
              <th>Equipos</th>
              <th>Ronda</th>
              <th>Estadio</th>
              <th>Ciudad</th>
              <th>Precio Mínimo</th>
              <th style={{ backgroundColor: '#2a2a2a', color: '#4CAF50', fontWeight: 'bold' }}>Categoría</th>
              <th>Cat 1</th>
              <th>Cat 2</th>
              <th>Cat 3</th>
              <th>Última Actualización</th>
            </tr>
          </thead>
          <tbody>
            {tickets.map((ticket) => {
              // Determinar de qué categoría viene el precio mínimo
              const getMinCategory = () => {
                if (!ticket.floorPrice || !ticket.prices) return 'N/A';
                
                const prices = ticket.prices;
                const floorPrice = ticket.floorPrice;
                
                // Comparar con tolerancia de 0.01 para manejar diferencias de redondeo
                const tolerance = 0.01;
                
                if (prices.cat1 && Math.abs(prices.cat1 - floorPrice) < tolerance) {
                  return 'Cat 1';
                }
                if (prices.cat2 && Math.abs(prices.cat2 - floorPrice) < tolerance) {
                  return 'Cat 2';
                }
                if (prices.cat3 && Math.abs(prices.cat3 - floorPrice) < tolerance) {
                  return 'Cat 3';
                }
                
                // Si no hay coincidencia exacta, encontrar el mínimo
                const validPrices = [];
                if (prices.cat1 && prices.cat1 > 0) validPrices.push({ cat: 'Cat 1', price: prices.cat1 });
                if (prices.cat2 && prices.cat2 > 0) validPrices.push({ cat: 'Cat 2', price: prices.cat2 });
                if (prices.cat3 && prices.cat3 > 0) validPrices.push({ cat: 'Cat 3', price: prices.cat3 });
                
                if (validPrices.length === 0) return 'N/A';
                
                const minPrice = Math.min(...validPrices.map(p => p.price));
                const minCat = validPrices.find(p => Math.abs(p.price - minPrice) < tolerance);
                
                return minCat ? minCat.cat : 'N/A';
              };
              
              const minCategory = getMinCategory();
              
              // Debug: verificar que la categoría se calcule
              if (ticket.matchNo === 1) {
                console.log('🔍 Debug Categoría M1:', {
                  floorPrice: ticket.floorPrice,
                  prices: ticket.prices,
                  minCategory: minCategory
                });
              }
              
              return (
                <tr key={ticket.matchNo}>
                  <td className="match-number">
                    <span className="match-number-text">M{ticket.matchNo}</span>
                  </td>
                  <td className="match-teams">{ticket.teams}</td>
                  <td className="match-round">{ticket.roundInfo || ticket.round || 'TBD'}</td>
                  <td className="match-stadium">{ticket.stadium}</td>
                  <td className="match-city">{ticket.city}</td>
                  <td className="match-price">
                    ${ticket.floorPrice?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || 'N/A'}
                  </td>
                  <td className="match-category" style={{ 
                    fontWeight: 'bold', 
                    color: '#4CAF50', 
                    fontSize: '14px', 
                    padding: '8px',
                    backgroundColor: '#1a1a1a',
                    textAlign: 'center',
                    minWidth: '80px'
                  }}>
                    <span style={{ 
                      display: 'inline-block',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      backgroundColor: '#2a2a2a',
                      border: '1px solid #4CAF50'
                    }}>
                      {minCategory || 'N/A'}
                    </span>
                  </td>
                  <td className="match-price-cat">
                    {ticket.prices?.cat1 ? `$${ticket.prices.cat1.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : 'N/A'}
                  </td>
                  <td className="match-price-cat">
                    {ticket.prices?.cat2 ? `$${ticket.prices.cat2.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : 'N/A'}
                  </td>
                  <td className="match-price-cat">
                    {ticket.prices?.cat3 ? `$${ticket.prices.cat3.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : 'N/A'}
                  </td>
                  <td className="match-updated">
                    {ticket.lastUpdated 
                      ? new Date(ticket.lastUpdated).toLocaleString('es-ES', { 
                          day: '2-digit', 
                          month: '2-digit', 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        })
                      : 'N/A'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      
      {/* Disclaimer Legal */}
      <div className="matches-disclaimer">
        <div className="disclaimer-icon">⚠️</div>
        <div className="disclaimer-content">
          <strong>Aviso Legal:</strong> RTB Collect no está afiliado, patrocinado ni respaldado por FIFA. 
          Este servicio proporciona información de mercado únicamente con fines informativos. 
          Los precios pueden tener retrasos y no constituyen asesoramiento financiero.
        </div>
      </div>
    </div>
  );
}

// Componente antiguo de MatchesTable (RTB) - mantenido por compatibilidad pero ya no se usa
function MatchesTable({ userProfile }) {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const userPlan = userProfile?.plan || 'basico';

  useEffect(() => {
    const fetchMatches = async () => {
      // Solo mostrar tabla para planes premium y elite
      if (userPlan === 'basico') {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const response = await apiRequest('/api/monitoring/matches');
        
        console.log('🏆 Respuesta de matches API:', response);
        
        if (Array.isArray(response)) {
          setMatches(response);
        } else if (response.success && Array.isArray(response.data)) {
          setMatches(response.data);
        } else {
          console.error('❌ Estructura de respuesta inesperada:', response);
          setError('No se pudieron cargar los partidos');
        }
      } catch (err) {
        console.error('Error fetching matches:', err);
        setError('Error al cargar los partidos');
      } finally {
        setLoading(false);
      }
    };

    fetchMatches();
  }, [userPlan]);

  // No mostrar nada para plan básico
  if (userPlan === 'basico') {
    return null;
  }

  if (loading) {
    return (
      <div className="matches-table-container">
        <h3>🏆 Partidos Disponibles</h3>
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Cargando partidos...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="matches-table-container">
        <h3>🏆 Partidos Disponibles</h3>
        <div className="error-state">
          <div className="error-icon">⚠️</div>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (!matches.length) {
    return (
      <div className="matches-table-container">
        <h3>🏆 Partidos Disponibles</h3>
        <div className="empty-state">
          <div className="empty-icon">⚽</div>
          <p>No hay partidos disponibles en este momento</p>
        </div>
      </div>
    );
  }

  return (
    <div className="matches-table-container">
      <h3>🏆 Partidos Disponibles ({matches.length})</h3>
      <div className="matches-table-wrapper">
                 <div className="scroll-indicator">
           <span>📊 {matches.length} partidos disponibles</span>
           <span>Desliza horizontal | Scroll vertical</span>
         </div>
        <table className="matches-table">
          <thead>
            <tr>
              <th>Partido</th>
              <th>Rivales</th>
              <th>Estadio</th>
              <th>Ciudad</th>
              <th>Precio Mínimo</th>
              {userPlan === 'elite' && <th>Acciones</th>}
            </tr>
          </thead>
          <tbody>
            {matches.map((match) => (
              <tr key={match.matchNo}>
                <td className="match-number">
                  <span className="match-number-text">M{match.matchNo}</span>
                </td>
                <td className="match-teams">{match.teams}</td>
                <td className="match-stadium">{match.stadium}</td>
                <td className="match-city">{match.city}</td>
                <td className="match-price">
                  ${match.floorPrice?.toLocaleString() || 'N/A'}
                </td>
                                 {userPlan === 'elite' && (
                   <td>
                     <div className="match-actions">
                       <button
                         className="buy-btn"
                         onClick={() => window.open(`https://collect.fifa.com/marketplace?tags=rtt-m${match.matchNo}`, '_blank')}
                       >
                         Comprar
                       </button>
                     </div>
                   </td>
                 )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {/* Disclaimer Legal */}
      <div className="matches-disclaimer">
        <div className="disclaimer-icon">⚠️</div>
        <div className="disclaimer-content">
          <strong>Aviso Legal:</strong> RTB Collect no está afiliado, patrocinado ni respaldado por FIFA. 
          Este servicio proporciona información de mercado únicamente con fines informativos. 
          Los precios pueden tener retrasos y no constituyen asesoramiento financiero.
        </div>
      </div>
    </div>
  );
}

// Modal para configurar frecuencia de monitoreo
function FrequencyModal({ userProfile, currentUser, onClose, updateUserProfile }) {
  const [selectedFrequency, setSelectedFrequency] = useState(userProfile?.configuracion?.frecuencia || 60);
  const [loading, setLoading] = useState(false);
  
  const currentPlan = userProfile?.plan || 'basico';
  
  // Definir límites por plan
  const planLimits = {
    basico: { minFrequency: 60, name: 'Básico' },
    premium: { minFrequency: 10, name: 'Premium' },
    elite: { minFrequency: 5, name: 'Elite' }
  };
  
  const currentLimit = planLimits[currentPlan];
  
  // Opciones de frecuencia disponibles según el plan
  const getAvailableFrequencies = () => {
    const allFrequencies = [
      { value: 5, label: '5 minutos', plan: 'elite' },
      { value: 10, label: '10 minutos', plan: 'premium' },
      { value: 15, label: '15 minutos', plan: 'premium' },
      { value: 30, label: '30 minutos', plan: 'premium' },
      { value: 60, label: '1 hora', plan: 'basico' },
      { value: 120, label: '2 horas', plan: 'basico' },
      { value: 180, label: '3 horas', plan: 'basico' },
      { value: 360, label: '6 horas', plan: 'basico' },
      { value: 720, label: '12 horas', plan: 'basico' },
      { value: 1440, label: '24 horas', plan: 'basico' }
    ];
    
    return allFrequencies.filter(freq => freq.value >= currentLimit.minFrequency);
  };
  
  const availableFrequencies = getAvailableFrequencies();
  
  const handleSave = async () => {
    if (selectedFrequency < currentLimit.minFrequency) {
      toast.error(`La frecuencia mínima para el plan ${currentLimit.name} es ${currentLimit.minFrequency} minutos`);
      return;
    }
    
    try {
      setLoading(true);
      
      // Debug: Log antes de enviar
      console.log('🔧 Actualizando frecuencia:', {
        partidos: userProfile?.configuracion?.partidos?.length || 0,
        frecuencia: selectedFrequency,
        planActual: userProfile?.plan,
        userId: currentUser?.uid
      });

      // Actualizar configuración en el backend
      const response = await apiRequest('/api/users/config', {
        method: 'PUT',
        body: {
          partidos: userProfile?.configuracion?.partidos || [],
          umbrales: userProfile?.configuracion?.umbrales || {},
          frecuencia: selectedFrequency,
          activo: userProfile?.configuracion?.activo !== false
        }
      });

      console.log('📡 Respuesta del backend:', response);
      
      if (response.success) {
        toast.success(`Frecuencia actualizada a ${selectedFrequency} minutos`);
        
        // Actualizar el perfil del usuario
        console.log('🔄 Actualizando perfil del usuario...');
        const updatedProfile = await updateUserProfile(currentUser.uid);
        console.log('✅ Perfil actualizado:', {
          plan: updatedProfile?.plan,
          frecuencia: updatedProfile?.configuracion?.frecuencia,
          partidos: updatedProfile?.configuracion?.partidos?.length
        });
        
        onClose();
      }
    } catch (error) {
      console.error('Error actualizando frecuencia:', error);
      toast.error('Error al actualizar la frecuencia');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content frequency-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>⏱️ Configurar Frecuencia de Monitoreo</h2>
          <button className="modal-close" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <div className="modal-body">
          <div className="frequency-info">
            <div className="plan-info">
              <span className="plan-badge">{currentLimit.name}</span>
              <span className="limit-text">
                Frecuencia mínima: {currentLimit.minFrequency} minutos
              </span>
            </div>
            <p>
              Selecciona con qué frecuencia quieres que revisemos los precios de tus partidos monitoreados.
            </p>
          </div>

          <div className="frequency-options">
            <h3>Opciones Disponibles</h3>
            <div className="frequency-grid">
              {availableFrequencies.map((freq) => (
                <label key={freq.value} className="frequency-option">
                  <input
                    type="radio"
                    name="frequency"
                    value={freq.value}
                    checked={selectedFrequency === freq.value}
                    onChange={(e) => setSelectedFrequency(parseInt(e.target.value))}
                  />
                  <div className="frequency-card">
                    <div className="frequency-label">{freq.label}</div>
                    <div className="frequency-description">
                      {freq.value < 60 ? 
                        `Revisión cada ${freq.value} min` : 
                        freq.value === 60 ? 
                          'Revisión cada hora' :
                          `Revisión cada ${freq.value / 60} horas`
                      }
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div className="frequency-preview">
            <h4>Vista Previa</h4>
            <div className="preview-card">
              <div className="preview-item">
                <span>Frecuencia seleccionada:</span>
                <strong>{availableFrequencies.find(f => f.value === selectedFrequency)?.label}</strong>
              </div>
              <div className="preview-item">
                <span>Revisiones por día:</span>
                <strong>{Math.round(1440 / selectedFrequency)} veces</strong>
              </div>
            </div>
          </div>
        </div>

        <div className="modal-actions">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancelar
          </button>
          <button 
            type="button" 
            className="btn btn-primary" 
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? 'Guardando...' : 'Guardar Cambios'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Componente de Monitoreo de Tickets
function TicketsTab({ userProfile }) {
  return (
    <div className="tab-content">
      <div className="tab-header">
        <h2>Monitoreo de Tickets</h2>
        <p>Precios actualizados de Tickets por partido</p>
      </div>
      <TicketsTable userProfile={userProfile} />
    </div>
  );
}

